/**
 * Oversikt → Oppfølging, one queue across dates (manager redesign, 2026-10-09). Built only from
 * what the API already holds; nothing here decides or writes. The unit is the POINT: one row a
 * manager can act on. A worklist ("5 registreringer til gjennomgang") is one point however many
 * records it holds; the number of records is said on the row. The total, the group headings and
 * the tab's badge all count points. What waits on an employee is listed apart and not counted.
 */
import type { ApiAbsence, ApiOffer, ApiShift, ApiSwap, ApiUser, ApiWorkTimeChange } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { MONTHS, fmtD, fullName, typeName } from '@/ui/format';
import { addDays } from '@timefly/core';
import { setupGaps } from '../ansatt/roster';
import type { Kind } from '../fravaer/AbsenceRange';
import { cancelIdsOf } from '../fravaer/queue';
import { clashesOf } from '../timeliste/approve';
import { kindOf } from '../timeliste/close';
import { closeWindow, monthOf, timeItems } from '../timeliste/summary';
import { toVaktplan } from '../vaktplan/link';
import { absenceSpan, affected, overlaps, spanText } from './cover';
import type { DayRow } from './day';

export type Group = 'oppmote' | 'foresporsler' | 'plan' | 'timer' | 'oppsett';
export const GROUPS: [Group, string][] = [
  ['oppmote', 'Oppmøte i dag'],
  ['foresporsler', 'Forespørsler'],
  ['plan', 'Plan og dekning'],
  ['timer', 'Timer'],
  ['oppsett', 'Ansattoppsett'],
];

export type Decision = { kind: 'offer' | 'swap'; id: number; done: string };
export interface Point {
  key: string;
  group: Group;
  /** the small line above the title: when it applies, and whether it is a worklist */
  meta: string;
  title: string;
  sub: string;
  /** records behind a worklist point; absent for a single case */
  records?: number;
  tone: 'warn' | 'dgr' | 'neu';
  /** where the decision is made: a link into the page that owns it, or the request's own two answers */
  to: string;
  label: string;
  decision?: Decision;
  /** sort key inside the group: the date it concerns */
  date: string;
  /** the shift a single case is about, for the plan to mark it (issue #147) */
  shiftId?: number;
  /** the person a single case is about */
  userId?: number;
  /** the request itself, for the case to draw who gives and who takes which shift */
  offer?: ApiOffer;
  swap?: ApiSwap;
  /** an approved absence the employee asks to cancel: its days and what approving it charged */
  cancel?: {
    from: string;
    to: string;
    type: string;
    deductible: boolean;
    /** days taken from the balance when it was approved, the part in next year's apart; null not charged */
    charged: number | null;
    chargedNext: number | null;
  };
  /** the setup worklist: each person and what they miss */
  gaps?: { name: string; missing: string[] }[];
  /** a worklist of shifts that may be approved together; absent when one falls on an absence day (#27) */
  shiftIds?: number[];
  /** a pending absence request, for answering it in place (issue #147); `deductible` from its type */
  absence?: {
    id: number;
    typeId: number;
    type: string;
    kind: Kind;
    start: string;
    end: string;
    deductible: boolean;
    /** false: the type was deleted after the request was sent; it is changed or declined, never approved */
    typeActive: boolean;
  };
}

export interface QueueInput {
  today: string;
  now: number;
  /** today's rows from day.ts */
  todayRows: DayRow[];
  /** shifts from today to a week ahead, assigned and open */
  ahead: ApiShift[];
  /** the month-close window (timeliste/summary.ts closeWindow) */
  closeShifts: ApiShift[];
  users: ApiUser[];
  absences: ApiAbsence[];
  kindOf: (a: ApiAbsence) => Kind;
  cancelRequests: ApiWorkTimeChange[];
  pending: PendingLike | null;
  can: {
    time: boolean;
    absence: boolean;
    setup: boolean;
    plan: boolean;
    planEdit: boolean;
  };
  rota: boolean;
  /** the company's punch tolerance in minutes, 15 when unknown */
  tol?: number;
  /** this week's rule breaches, worked out by the page with the company's rules */
  breaches?: { count: number; sub: string; monday: string } | null;
}
type PendingLike = { shiftOffers?: ApiOffer[]; shiftSwaps?: ApiSwap[] };

const longDate = (d: string) => `${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1]}`;
const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const hm = (s: ApiShift | null) => (s ? `${oslo(s.start_time)?.time}–${oslo(s.end_time)?.time}` : '');
const who = (u: Pick<ApiUser, 'name' | 'lastname'> | null | undefined) => fullName(u) || T('Ukjent');
const plural = (n: number, one: string, many: string) => (n === 1 ? T(one, { n }) : T(many, { n }));
/** "I dag", "Neste 7 dager", "Tidligere": when a point applies, said the same way on every row. */
const horizon = (d: string, today: string) =>
  d === today
    ? T('I dag')
    : d < today
      ? T('Tidligere')
      : d <= addDays(today, 7)
        ? T('Neste 7 dager')
        : T('Senere');

export interface Queue {
  points: Point[];
  waiting: Point[];
}

export function buildQueue(x: QueueInput): Queue {
  const points: Point[] = [];
  const waiting: Point[] = [];
  const { today } = x;

  // Timer: an open punch is a case of its own, a shift that ended with the clock still running,
  // fixed by putting in the actual end (issue #147); the month-close worklists hold the rest, so no
  // shift is counted twice (timeliste/close.ts kindOf decides 'open', as Timeliste does)
  const openPunch = x.can.time
    ? x.closeShifts.filter((s) => s.user_id != null && kindOf(s, x.now, x.tol ?? 15) === 'open')
    : [];
  // today's row for a shift with an open punch is the same task: one case, the open punch, under
  // Nå when it is today's (review of PR #150, F2)
  const openIds = new Set(openPunch.map((s) => s.id));
  const todayIds = new Set(x.todayRows.map((r) => r.shift.id));

  // Oppmøte i dag: each row the day list marks for a look, one point each
  if (x.can.time)
    for (const r of x.todayRows.filter((r) => r.review && !openIds.has(r.shift.id)))
      points.push({
        key: `day${r.shift.id}`,
        group: 'oppmote',
        meta: `${T('Avklar i dag')} · ${longDate(today)}`,
        title: `${who(r.shift.user)} · ${r.label}`,
        sub: r.explain,
        tone: 'warn',
        to: `/hjem?valgt=${r.shift.id}`,
        label: 'Avklar',
        date: today,
        shiftId: r.shift.id,
        ...(r.shift.user_id != null ? { userId: r.shift.user_id } : {}),
      });

  // Forespørsler: absence requests, cancellation requests, claims and swaps both sides agreed to
  if (x.can.absence) {
    for (const a of x.absences.filter((a) => a.status === 'pending')) {
      const from = oslo(a.start_date)?.date ?? '';
      const to = oslo(a.end_date)?.date ?? from;
      const span = absenceSpan(a, x.kindOf(a));
      const over = x.ahead.filter((s) => s.user_id === a.user_id && overlaps(span, s)).length;
      points.push({
        key: `abs${a.id}`,
        group: 'foresporsler',
        meta: `${horizon(from, today)} · ${longDate(from)}${a.created_at ? ` · Mottatt ${longDate(oslo(a.created_at)?.date ?? '')}` : ''}`,
        title: T('Fravær: {who}', { who: who(a.user) }),
        sub: `${typeName(a.absence_type)} · ${fmtD(from)}${to !== from ? ` ${T('til {day}', { day: fmtD(to) })}` : ''} · ${T('venter på svar')}${over ? ` · ${plural(over, '{n} vakt berørt', '{n} vakter berørt')}` : ''}`,
        tone: over ? 'warn' : 'neu',
        to: `/fravaer/soknader?apne=${a.id}`,
        label: 'Vurder forespørsel',
        date: from,
        userId: a.user_id,
        // without its type the deduction is not known: no answer in place, only the way to Fravær
        ...(a.absence_type
          ? {
              absence: {
                id: a.id,
                typeId: a.absence_type.id,
                type: typeName(a.absence_type),
                kind: x.kindOf(a),
                start: a.start_date,
                end: a.end_date,
                deductible: a.absence_type.is_deductible === true,
                typeActive: a.absence_type.is_active !== false,
              },
            }
          : {}),
      });
    }
    const cancelIds = cancelIdsOf(x.cancelRequests, x.absences);
    for (const a of x.absences.filter((a) => cancelIds.has(a.id))) {
      const from = oslo(a.start_date)?.date ?? '';
      const to = oslo(a.end_date)?.date ?? from;
      if (to < today) continue;
      points.push({
        key: `cancel${a.id}`,
        group: 'foresporsler',
        meta: `${horizon(from, today)} · ${longDate(from)}`,
        title: T('{who} vil avlyse {type}', {
          who: who(a.user),
          type: typeName(a.absence_type).toLowerCase(),
        }),
        sub: `${fmtD(from)}${to !== from ? ` ${T('til {day}', { day: fmtD(to) })}` : ''}. ${T('Godkjent fravær; avlysningen venter på svar.')}`,
        tone: 'warn',
        to: `/fravaer/soknader?apne=${a.id}`,
        label: 'Vurder avlysning',
        date: from,
        userId: a.user_id,
        cancel: {
          from,
          to,
          type: typeName(a.absence_type),
          deductible: a.absence_type?.is_deductible === true,
          charged: a.charged_days ?? null,
          chargedNext: a.charged_days_next_year ?? null,
        },
      });
    }
  }
  if (x.can.plan) {
    const offers = x.pending?.shiftOffers ?? [];
    for (const o of offers) {
      const claim = o.offered_by === o.accepted_by;
      if (claim && o.shift && o.shift.user_id != null) continue;
      const d = o.shift ? dateOf(o.shift) : today;
      points.push({
        key: `offer${o.id}`,
        group: 'foresporsler',
        meta: `${horizon(d, today)} · ${longDate(d)}`,
        title: claim
          ? T('{who} vil ta den ledige vakten', { who: who(o.acceptedByUser) })
          : T('{who} gir vakten til {to}', { who: who(o.offeredByUser), to: who(o.acceptedByUser) }),
        sub: `${hm(o.shift)}${o.shift?.shiftType?.name ? ` · ${o.shift.shiftType.name}` : ''}. ${T('Se vakten før du svarer.')}`,
        tone: 'neu',
        to: toVaktplan({ week: d }),
        label: 'Vurder',
        decision: { kind: 'offer', id: o.id, done: claim ? 'Vakten er tildelt' : 'Vakten er flyttet' },
        offer: o,
        date: d,
        ...(o.shift_id != null ? { shiftId: o.shift_id } : {}),
      });
    }
    for (const sw of x.pending?.shiftSwaps ?? []) {
      const d = sw.shiftOne ? dateOf(sw.shiftOne) : today;
      points.push({
        key: `swap${sw.id}`,
        group: 'foresporsler',
        meta: `${horizon(d, today)} · ${longDate(d)}`,
        title: T('{one} og {two} vil bytte', {
          one: who(sw.offeredByUserOne),
          two: who(sw.offeredByUserTwo),
        }),
        sub: `${hm(sw.shiftOne)} ${T('og')} ${sw.shiftTwo ? `${longDate(dateOf(sw.shiftTwo))} ${hm(sw.shiftTwo)}` : ''}. ${T('Begge har sagt ja.')}`,
        tone: 'neu',
        to: toVaktplan({ week: d }),
        label: 'Vurder',
        decision: { kind: 'swap', id: sw.id, done: 'Byttet er godkjent' },
        swap: sw,
        date: d,
        ...(sw.shift_id_one != null ? { shiftId: sw.shift_id_one } : {}),
      });
    }
    // waiting on an employee: offered and nobody took it, a swap the other side has not answered
    const taken = new Set(offers.map((o) => o.shift_id));
    const swapping = new Set((x.pending?.shiftSwaps ?? []).flatMap((s) => [s.shift_id_one, s.shift_id_two]));
    for (const s of (x.pending ? x.ahead : []).filter((s) => dateOf(s) >= today)) {
      if (s.is_offer_requested && !taken.has(s.id))
        waiting.push({
          key: `offered${s.id}`,
          group: 'foresporsler',
          meta: `${horizon(dateOf(s), today)} · ${longDate(dateOf(s))}`,
          title: T('{who} har tilbudt bort vakten', { who: who(s.user) }),
          sub: `${hm(s)}. ${T('Ingen har tatt den ennå.')}`,
          tone: 'neu',
          to: toVaktplan({ week: dateOf(s) }),
          label: 'Se vakten',
          date: dateOf(s),
          shiftId: s.id,
          ...(s.user_id != null ? { userId: s.user_id } : {}),
        });
      if (s.is_swap_requested && !swapping.has(s.id))
        waiting.push({
          key: `swapreq${s.id}`,
          group: 'foresporsler',
          meta: `${horizon(dateOf(s), today)} · ${longDate(dateOf(s))}`,
          title: T('{who} vil bytte vakt', { who: who(s.user) }),
          sub: `${hm(s)}. ${T('Venter på at den andre sier ja.')}`,
          tone: 'neu',
          to: toVaktplan({ week: dateOf(s) }),
          label: 'Se vakten',
          date: dateOf(s),
          shiftId: s.id,
          ...(s.user_id != null ? { userId: s.user_id } : {}),
        });
    }
  }

  // Plan og dekning: approved absence on planned work (the affected hours only), open shifts and drafts
  if (x.rota && x.can.plan) {
    const week = x.ahead.filter((s) => dateOf(s) >= today && dateOf(s) <= addDays(today, 7));
    for (const a of x.absences.filter((a) => a.status === 'accepted'))
      for (const f of affected(a, x.kindOf(a), week)) {
        const d = oslo(f.from)?.date ?? today;
        points.push({
          key: `cover${a.id}-${f.shift.id}`,
          group: 'plan',
          meta: `${horizon(d, today)} · ${longDate(d)}`,
          title: T('Godkjent fravær · vakt må dekkes'),
          sub: `${who(a.user)} · ${T('berørt {span}', { span: spanText(f) })}`,
          tone: 'warn',
          to: x.can.planEdit
            ? toVaktplan({ day: d, newShift: { from: oslo(f.from)?.time ?? '', to: oslo(f.to)?.time ?? '' } })
            : toVaktplan({ day: d }),
          label: 'Finn dekning',
          date: d,
          shiftId: f.shift.id,
          userId: a.user_id,
        });
      }
    if (x.breaches?.count)
      points.push({
        key: 'breaches',
        group: 'plan',
        meta: `${T('Denne uken')} · ${T('Arbeidsliste')}`,
        title: plural(x.breaches.count, '{n} vakt bryter en regel', '{n} vakter bryter en regel'),
        sub: x.breaches.sub,
        records: x.breaches.count,
        tone: 'warn',
        to: toVaktplan({ week: x.breaches.monday }),
        label: 'Se reglene',
        date: x.breaches.monday,
      });
    const open = week.filter((s) => s.user_id == null);
    if (open.length)
      points.push({
        key: 'open',
        group: 'plan',
        meta: `${T('Neste 7 dager')} · ${T('Arbeidsliste')}`,
        title: plural(open.length, '{n} ledig vakt uten ansatt', '{n} ledige vakter uten ansatt'),
        sub: open
          .slice(0, 4)
          .map((s) => `${longDate(dateOf(s))} ${hm(s)}`)
          .join(' · '),
        records: open.length,
        tone: 'dgr',
        to: toVaktplan({ week: dateOf(open[0] as ApiShift) }),
        label: 'Se ledige vakter',
        date: dateOf(open[0] as ApiShift),
      });
    const drafts = week.filter((s) => !s.published);
    if (drafts.length)
      points.push({
        key: 'drafts',
        group: 'plan',
        meta: `${T('Neste 7 dager')} · ${T('Arbeidsliste')}`,
        title: plural(drafts.length, '{n} vakt er ikke publisert', '{n} vakter er ikke publisert'),
        sub: T('Åpne planen og se gjennom før du deler vaktene med teamet.'),
        records: drafts.length,
        tone: 'neu',
        to: toVaktplan({ week: dateOf(drafts[0] as ApiShift) }),
        label: 'Se utkast',
        date: dateOf(drafts[0] as ApiShift),
      });
  }

  for (const s of openPunch)
    points.push({
      key: `openpunch${s.id}`,
      group: todayIds.has(s.id) ? 'oppmote' : 'timer',
      meta: `${horizon(dateOf(s), today)} · ${longDate(dateOf(s))}`,
      title: T('{who} mangler utstempling', { who: who(s.user) }),
      sub: T(
        'Vakten er over, men sluttiden mangler. Oppgi faktisk sluttid og en begrunnelse før timene kan godkjennes.',
      ),
      tone: 'dgr',
      to: `/timeliste?vis=uten&mnd=${dateOf(s).slice(0, 7)}&ansatt=${s.user_id}&vakt=${s.id}`,
      label: 'Skriv inn riktig tid',
      date: dateOf(s),
      shiftId: s.id,
      ...(s.user_id != null ? { userId: s.user_id } : {}),
    });
  const notOpen = openIds;
  const closing = x.closeShifts.filter((s) => !notOpen.has(s.id));
  // this month's shifts ready to approve, as timeItems counts them; none when one falls on a day of
  // approved absence, which Timeliste asks about first (#27)
  const ready = closing.filter(
    (s) => s.user_id != null && monthOf(dateOf(s)) === monthOf(today) && kindOf(s, x.now) === 'ready',
  );
  const readyIds = clashesOf(ready, x.absences).length ? undefined : ready.map((s) => s.id);
  if (x.can.time)
    for (const it of timeItems(closing, today, x.now, x.tol)) {
      points.push({
        key: `time-${it.key}`,
        group: 'timer',
        meta: `${it.key === 'prev-month' ? T('Tidligere periode') : T('Denne måneden')} · ${T('Arbeidsliste')}`,
        title: it.label,
        sub: it.note,
        records: it.count,
        // Missing punches use the same review colour as Dagen and Timer.
        tone: it.key === 'missing' || it.key === 'open' ? 'warn' : it.tone === 'bad' ? 'dgr' : 'warn',
        to: `${it.to.replace(/vis=[^&]+/, 'vis=alle')}&arbeidsliste=${it.key}`,
        label: 'Åpne arbeidsliste',
        date: it.key === 'prev-month' ? addDays(today, -31) : today,
        ...(it.key === 'ready' && readyIds?.length === it.count ? { shiftIds: readyIds } : {}),
      });
    }

  // Ansattoppsett: everyone with a setup gap, one worklist, the same rule as the roster
  if (x.can.setup) {
    const gaps = x.users.filter((u) => u.active_status && setupGaps(u, true).length);
    if (gaps.length)
      points.push({
        key: 'setup',
        group: 'oppsett',
        meta: `${T('Oppsett')} · ${T('Arbeidsliste')}`,
        title: plural(gaps.length, '{n} ansatt mangler opplysninger', '{n} ansatte mangler opplysninger'),
        sub: `${gaps
          .slice(0, 4)
          .map((u) => fullName(u))
          .join(', ')}. ${T('Ansattnummer, e-post, fødselsdato og avtalt arbeidstid.')}`,
        records: gaps.length,
        gaps: gaps.map((u) => ({ name: fullName(u), missing: setupGaps(u, true).map((g) => T(g)) })),
        tone: 'warn',
        to: '/ansatte?oppsett=mangler',
        label: 'Se ansatte med mangler',
        date: today,
      });
  }

  const byDate = (a: Point, b: Point) => a.date.localeCompare(b.date);
  return { points: points.sort(byDate), waiting: waiting.sort(byDate) };
}

/** The shifts the queue reads, from the month-close window's start to a week ahead. */
export const queueWindow = (today: string) => ({
  from: closeWindow(today).from,
  to: addDays(today, 8),
});
